import { useState, useEffect } from 'react';
import {
  X,
  ChevronRight,
  ChevronLeft,
  Check,
  Copy,
  ExternalLink,
  AlertCircle,
  Loader2,
  CheckCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { integrationsAPI } from '../../services/api';

export default function GHLSetupWizard({
  isOpen,
  onClose,
  onComplete,
  isReconfigure = false
}) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [callbackUrl, setCallbackUrl] = useState('');
  const [credentials, setCredentials] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  const totalSteps = 4;

  useEffect(() => {
    if (isOpen && isReconfigure) {
      loadExistingConfig();
    }
  }, [isOpen, isReconfigure]);

  const loadExistingConfig = async () => {
    try {
      const response = await integrationsAPI.getConfig('ghl');
      if (response.data.config) {
        setLocationId(response.data.config.locationId || '');
        setCampaignId(response.data.config.campaignId || '');
        setCallbackUrl(response.data.config.callbackUrl || '');
      }
    } catch (error) {
      console.error('Failed to load config:', error);
    }
  };

  const handleGenerateCredentials = async () => {
    if (!locationId.trim()) {
      toast.error('Please enter your GHL Location ID');
      return;
    }

    setLoading(true);
    try {
      const response = await integrationsAPI.setupGHL({
        locationId: locationId.trim(),
        campaignId: campaignId.trim() || undefined,
        callbackUrl: callbackUrl.trim() || undefined
      });
      setCredentials(response.data);
      setStep(2);
      toast.success('Credentials generated!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to generate credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!callbackUrl.trim()) {
      toast.error('Please enter a callback URL to test');
      return;
    }

    setTestLoading(true);
    setTestResult(null);
    try {
      const response = await integrationsAPI.test('ghl', {
        callbackUrl: callbackUrl.trim()
      });
      setTestResult({ success: true, message: response.data.message });
      toast.success('Test callback sent!');
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.error || 'Test failed'
      });
      toast.error('Test failed');
    } finally {
      setTestLoading(false);
    }
  };

  const copyToClipboard = (text, label = 'Copied!') => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const handleComplete = () => {
    onComplete();
    toast.success('GHL integration setup complete!');
  };

  if (!isOpen) return null;

  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Enter Your GHL Location ID
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          You can find this in GHL under Settings → Business Info → Location ID
        </p>
        <input
          type="text"
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
          placeholder="e.g., loc_abc123xyz"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Campaign ID <span className="text-gray-400">(optional)</span>
        </label>
        <input
          type="text"
          value={campaignId}
          onChange={(e) => setCampaignId(e.target.value)}
          placeholder="Leave blank for default campaign"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
        />
      </div>

      <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">Where to find your Location ID:</p>
            <ol className="list-decimal list-inside space-y-1 text-blue-600">
              <li>Log into GoHighLevel</li>
              <li>Go to Settings (gear icon)</li>
              <li>Click "Business Info"</li>
              <li>Copy the "Location ID" value</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">
          Credentials Generated!
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Save your API key securely - it won't be shown again
        </p>
      </div>

      {credentials && (
        <div className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">API Key</span>
              <button
                onClick={() => copyToClipboard(credentials.apiKey, 'API Key copied!')}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
              >
                <Copy className="w-4 h-4" />
                Copy
              </button>
            </div>
            <code className="block w-full p-3 bg-white border border-gray-200 rounded text-sm font-mono break-all">
              {credentials.apiKey}
            </code>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Webhook URL</span>
              <button
                onClick={() => copyToClipboard(credentials.webhookUrl, 'Webhook URL copied!')}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
              >
                <Copy className="w-4 h-4" />
                Copy
              </button>
            </div>
            <code className="block w-full p-3 bg-white border border-gray-200 rounded text-sm font-mono break-all">
              {credentials.webhookUrl}
            </code>
          </div>
        </div>
      )}

      <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-100">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-700">
            <p className="font-medium">Important:</p>
            <p>Save your API key now. For security, we only show it once.</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Configure Your GHL Workflow
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Add a "Send Webhook" action in your GHL workflow with these settings:
        </p>
      </div>

      {credentials && (
        <div className="space-y-4">
          {/* Webhook URL */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">1. Webhook URL</span>
              <button
                onClick={() => copyToClipboard(credentials.webhookUrl)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
              >
                <Copy className="w-3 h-3" />
                Copy
              </button>
            </div>
            <code className="block w-full p-3 bg-gray-900 text-green-400 rounded text-sm font-mono break-all">
              {credentials.webhookUrl}
            </code>
          </div>

          {/* Headers */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">2. Headers</span>
              <button
                onClick={() => copyToClipboard(
                  Object.entries(credentials.headers).map(([k, v]) => `${k}: ${v}`).join('\n'),
                  'Headers copied!'
                )}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
              >
                <Copy className="w-3 h-3" />
                Copy All
              </button>
            </div>
            <pre className="w-full p-3 bg-gray-900 text-green-400 rounded text-sm font-mono overflow-x-auto">
{Object.entries(credentials.headers).map(([key, value]) => (
  `${key}: ${value}`
)).join('\n')}
            </pre>
          </div>

          {/* Payload Template */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">3. Payload (JSON Body)</span>
              <button
                onClick={() => copyToClipboard(
                  JSON.stringify(credentials.payloadTemplate, null, 2),
                  'Payload copied!'
                )}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
              >
                <Copy className="w-3 h-3" />
                Copy
              </button>
            </div>
            <pre className="w-full p-3 bg-gray-900 text-green-400 rounded text-sm font-mono overflow-x-auto max-h-48">
{JSON.stringify(credentials.payloadTemplate, null, 2)}
            </pre>
          </div>
        </div>
      )}

      <a
        href="https://docs.alfredvoice.ai/integrations/ghl"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
      >
        <ExternalLink className="w-4 h-4" />
        View detailed setup guide
      </a>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Test Your Connection
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Enter a callback URL to receive a test payload and verify everything works.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Callback URL
        </label>
        <input
          type="url"
          value={callbackUrl}
          onChange={(e) => setCallbackUrl(e.target.value)}
          placeholder="https://your-webhook-receiver.com/callback"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
        />
        <p className="text-xs text-gray-500 mt-2">
          This is where Alfred Voice will send call results. You can use a GHL inbound webhook URL.
        </p>
      </div>

      <button
        onClick={handleTestConnection}
        disabled={testLoading || !callbackUrl.trim()}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {testLoading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Sending Test...
          </>
        ) : (
          'Send Test Callback'
        )}
      </button>

      {testResult && (
        <div className={`p-4 rounded-lg ${
          testResult.success
            ? 'bg-green-50 border border-green-100'
            : 'bg-red-50 border border-red-100'
        }`}>
          <div className="flex items-start gap-3">
            {testResult.success ? (
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            )}
            <div className={`text-sm ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
              <p className="font-medium">{testResult.success ? 'Success!' : 'Test Failed'}</p>
              <p>{testResult.message}</p>
            </div>
          </div>
        </div>
      )}

      <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">Testing is optional</p>
            <p>You can complete setup now and test later from your GHL workflow.</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                GHL
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {isReconfigure ? 'Reconfigure' : 'Connect'} GoHighLevel
                </h2>
                <p className="text-sm text-gray-500">
                  Step {step} of {totalSteps}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="px-6 pt-4">
            <div className="flex gap-2">
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    s <= step ? 'bg-green-500' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t bg-gray-50 rounded-b-2xl">
            <button
              onClick={() => step > 1 ? setStep(step - 1) : onClose()}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              {step === 1 ? 'Cancel' : 'Back'}
            </button>

            {step === 1 && (
              <button
                onClick={handleGenerateCredentials}
                disabled={loading || !locationId.trim()}
                className="flex items-center gap-2 px-6 py-2 bg-black hover:bg-gray-800 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    Generate Credentials
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </button>
            )}

            {step === 2 && (
              <button
                onClick={() => setStep(3)}
                className="flex items-center gap-2 px-6 py-2 bg-black hover:bg-gray-800 text-white rounded-lg transition-colors"
              >
                Continue
                <ChevronRight className="w-4 h-4" />
              </button>
            )}

            {step === 3 && (
              <button
                onClick={() => setStep(4)}
                className="flex items-center gap-2 px-6 py-2 bg-black hover:bg-gray-800 text-white rounded-lg transition-colors"
              >
                Continue
                <ChevronRight className="w-4 h-4" />
              </button>
            )}

            {step === 4 && (
              <button
                onClick={handleComplete}
                className="flex items-center gap-2 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              >
                <Check className="w-4 h-4" />
                Complete Setup
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
